import { NUDGE_AUTO_HIDE_MS } from "./engine";

/**
 * Balonul de nudge: o propoziție discretă lângă bulină, cu un ✕. NU un pop-up -
 * nu acoperă pagina, nu fură focusul, nu blochează nimic și dispare singur
 * (docs/plan-commerce-proactivity.md → „Principiul: nudge, nu ambuscadă").
 * Diferența față de competiție e chiar asta, deci e o decizie de produs, nu de
 * stil: tot ce l-ar face mai „vizibil" (overlay, animație lungă, buton mare de
 * accept) îl transformă în lucrul pe care vizitatorul îl închide din reflex.
 *
 * Trăiește în shadow root-ul widgetului (`mountOverlay`), lângă toast-urile de
 * coș: aceeași izolare de CSS-ul magazinului și - la fel de important -
 * conținutul lui nu ajunge în snapshotul trimis modelului.
 */
export const NUDGE_CSS = `
/* Same shadow root as WIDGET_CSS, so the shop's own selectors cannot reach in and
   the reset on :host already applies. Fixed positioning resolves against the
   viewport here, the same way the cart cards and toasts do.
   It sits right above the launcher (46px + 20px offset + a small gap) and is
   capped in width so it reads as a remark next to the bubble, not as a banner. */
.aig-nudge { position: fixed; right: 20px; bottom: 78px; width: 280px; max-width: calc(100vw - 40px); z-index: 2147483020; display: flex; align-items: flex-start; gap: 6px; padding: 10px 10px 10px 12px; background: #fff; color: #111; border-radius: 12px; border: 1px solid #e2e8f0; box-shadow: 0 6px 22px rgba(0,0,0,.18); }
.aig-nudge[hidden] { display: none; }
/* It arrives, it does not pounce: a short fade with a few pixels of travel. Anything
   longer or bouncier reads as an ad. */
@keyframes aig-nudge-in { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
.aig-nudge { animation: aig-nudge-in .22s ease-out both; }
@media (prefers-reduced-motion: reduce) { .aig-nudge { animation: none; } }
/* The sentence itself is the click target - the whole point is that answering costs
   one click - but it must not look like a call-to-action button: no fill, no border,
   left-aligned, the same size as a chat message. */
.aig-nudge-text { appearance: none; -webkit-appearance: none; font-family: inherit; margin: 0; padding: 0; border: none; background: transparent; color: inherit; text-align: left; font-size: 12.5px; line-height: 1.45; cursor: pointer; flex: 1; min-width: 0; }
.aig-nudge-text:hover { color: var(--aig-primary-ink, #7a51d8); }
.aig-nudge-x { appearance: none; -webkit-appearance: none; font-family: inherit; margin: 0; flex-shrink: 0; width: 18px; height: 18px; padding: 0; border: none; background: transparent; color: #94a3b8; font-size: 12px; line-height: 1; cursor: pointer; display: flex; align-items: center; justify-content: center; }
.aig-nudge-x:hover { color: #475569; }
.aig-nudge-text:focus-visible, .aig-nudge-x:focus-visible { outline: 2px solid var(--aig-primary-ink, #7a51d8); outline-offset: 2px; border-radius: 6px; }
`;

export interface NudgeBalloonHandlers {
  /** Click pe propoziție: deschide panoul cu firul deja pornit (vezi sdk.ts). */
  onEngage: () => void;
  /** ✕: gata cu nudge-urile pe sesiunea asta. */
  onDismiss: () => void;
}

export class NudgeBalloon {
  private node?: HTMLDivElement;
  private timer?: ReturnType<typeof setTimeout>;

  /**
   * `mount` primește nodul o singură dată (widget.mountOverlay), la prima
   * afișare: un balon montat la boot ar sta ascuns în shadow root pe fiecare
   * pagină a fiecărui magazin, chiar dacă niciun trigger nu se aprinde vreodată.
   */
  constructor(private readonly mount: (node: HTMLElement) => void) {}

  /** Afișează balonul. Un al doilea apel îl înlocuiește pe primul, cu ceasul luat de la capăt. */
  show(text: string, dismissLabel: string, handlers: NudgeBalloonHandlers): void {
    this.clearTimer();
    if (!this.node) {
      this.node = document.createElement("div");
      this.node.className = "aig-nudge";
      // `status`, nu `alert`: un cititor de ecran îl anunță când termină ce
      // citea, nu îl întrerupe. Un nudge nu e o urgență.
      this.node.setAttribute("role", "status");
      this.mount(this.node);
    }
    // Rescris de la zero la fiecare afișare: textul, handlerele și eticheta lui
    // ✕ diferă de la un trigger la altul, iar un listener rămas de la balonul
    // precedent ar raporta triggerul greșit.
    this.node.textContent = "";
    this.node.hidden = false;

    const sentence = document.createElement("button");
    sentence.className = "aig-nudge-text";
    sentence.type = "button";
    // `textContent`, ca peste tot în widget: niciun HTML brut, nici măcar dintr-un
    // template al nostru.
    sentence.textContent = text;
    sentence.addEventListener("click", () => {
      this.hide();
      handlers.onEngage();
    });

    const close = document.createElement("button");
    close.className = "aig-nudge-x";
    close.type = "button";
    close.setAttribute("aria-label", dismissLabel);
    close.textContent = "✕";
    close.addEventListener("click", () => {
      this.hide();
      handlers.onDismiss();
    });

    this.node.append(sentence, close);
    // Dispare singur: un nudge care rămâne pe ecran devine un banner, iar
    // vizitatorul care l-a ignorat a răspuns deja - prin tăcere.
    this.timer = setTimeout(() => this.hide(), NUDGE_AUTO_HIDE_MS);
  }

  hide(): void {
    this.clearTimer();
    if (!this.node) return;
    this.node.hidden = true;
    this.node.textContent = "";
  }

  /** Vizibil ACUM - citit de teste și de gărzile din sdk.ts. */
  get visible(): boolean {
    return this.node !== undefined && !this.node.hidden;
  }

  private clearTimer(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = undefined;
  }
}
